// Security Sanitizer Service
// Sanitiza datos del formulario para cumplir con estándares OWASP

export class SecuritySanitizer {
  
  // Sanitiza texto para prevenir XSS (solo patrones realmente peligrosos)
  static sanitizeText(text) {
    if (!text || typeof text !== 'string') return text;
    
    let sanitized = text
      // Solo remover patrones realmente peligrosos, no caracteres legítimos
      .replace(/<script[^>]*>.*?<\/script>/gi, '') // Remover scripts completos
      .replace(/javascript:/gi, '') // Remover javascript:
      .replace(/on\w+\s*=/gi, '') // Remover event handlers
      .replace(/<iframe[^>]*>.*?<\/iframe>/gi, '') // Remover iframes completos
      .replace(/<object[^>]*>.*?<\/object>/gi, '') // Remover objects completos
      .replace(/<embed[^>]*>/gi, '') // Remover embeds
      .replace(/expression\s*\(/gi, '') // Remover CSS expressions
      .replace(/eval\s*\(/gi, '') // Remover eval
      .replace(/import\s*\(/gi, '') // Remover dynamic imports
      .trim();

    // Normalizar estilos HTML: remover font-family, font-size, background-color y otros estilos problemáticos
    sanitized = sanitized.replace(/style\s*=\s*["']([^"']*)["']/gi, (match, styleContent) => {
      // Remover font-family, font-size, background-color y otros estilos problemáticos
      const cleanedStyle = styleContent
        .replace(/font-family\s*:\s*[^;]+;?/gi, '') // Remover font-family
        .replace(/font-size\s*:\s*[0-9.]+pt;?/gi, '') // Remover font-size en pt
        .replace(/background-color\s*:\s*[^;]+;?/gi, '') // Remover background-color
        .replace(/;\s*;/g, ';') // Limpiar dobles punto y coma
        .replace(/^\s*;\s*|\s*;\s*$/g, '') // Remover punto y coma al inicio/final
        .trim();

      // Si el estilo queda vacío, remover el atributo completo
      if (!cleanedStyle) {
        return '';
      }
      return `style="${cleanedStyle}"`;
    });

    // Limpiar espacios en blanco extra que puedan quedar
    sanitized = sanitized.replace(/\s+/g, ' ').trim();

    return sanitized;
  }

  // Sanitiza números para prevenir SQL injection
  static sanitizeNumber(value) {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const parsed = parseFloat(value);
    return isNaN(parsed) ? null : parsed;
  }

  // Sanitiza IDs para prevenir SQL injection
  static sanitizeId(value) {
    if (!value) return null;
    
    // Solo permitir números y algunos caracteres seguros
    const sanitized = String(value).replace(/[^0-9a-zA-Z_-]/g, '');
    return sanitized || null;
  }

  // Sanitiza education_level (puede ser string del enum o número)
  static sanitizeEducationLevel(value) {
    if (!value) return null;
    
    const educationLevelMap = {
      'special_education': 0,
      'primary_incomplete': 1,
      'primary_complete': 2,
      'secondary_incomplete': 3,
      'secondary_complete': 4,
      'higher_incomplete': 5,
      'higher_complete': 6
    };
    
    // Si ya es un número, devolverlo
    if (typeof value === 'number') return value;
    
    // Si es un string del enum, mapearlo al número correspondiente
    if (typeof value === 'string' && educationLevelMap[value] !== undefined) {
      return educationLevelMap[value];
    }
    
    // Si es un string numérico, parsearlo
    const num = parseFloat(value);
    return isNaN(num) ? null : num;
  }

  // Sanitiza fechas
  static sanitizeDate(dateValue) {
    if (!dateValue) return null;
    
    try {
      const date = new Date(dateValue);
      if (isNaN(date.getTime())) return null;
      
      // Formatear como ISO string
      return date.toISOString().split('T')[0];
    } catch (e) {
      return null;
    }
  }

  // Sanitiza opciones de select
  static sanitizeOptions(options) {
    if (!Array.isArray(options)) return [];
    
    return options
      .map(option => this.sanitizeText(option))
      .filter(option => option && option.length > 0);
  }

  // Sanitiza objeto completo de datos del curso
  static sanitizeCourseData(courseData) {
    if (!courseData || typeof courseData !== 'object') return courseData;

    const sanitized = JSON.parse(JSON.stringify(courseData)); // Deep clone

    // Sanitizar campos de texto
    if (sanitized.course) {
      sanitized.course.title = this.sanitizeText(sanitized.course.title);
      sanitized.course.job_description = this.sanitizeText(sanitized.course.job_description);
      sanitized.course.vacancies = this.sanitizeNumber(sanitized.course.vacancies);
      
      // Sanitizar minisite_publication_attributes
      if (sanitized.course.minisite_publication_attributes) {
        const attrs = sanitized.course.minisite_publication_attributes;
        
        // Sanitizar campos de texto
        attrs.title = this.sanitizeText(attrs.title);
        attrs.address = this.sanitizeText(attrs.address);
        attrs.requesting_company = this.sanitizeText(attrs.requesting_company);
        attrs.location = this.sanitizeText(attrs.location);
        attrs.position_family = this.sanitizeText(attrs.position_family);
        attrs.vacancy_requester = this.sanitizeText(attrs.vacancy_requester);
        attrs.preferred_schedule = this.sanitizeText(attrs.preferred_schedule);
        attrs.preferred_labor_day = this.sanitizeText(attrs.preferred_labor_day);
        attrs.preferred_turn = this.sanitizeText(attrs.preferred_turn);
        attrs.dress_code = this.sanitizeText(attrs.dress_code);
        attrs.requirements = this.sanitizeText(attrs.requirements);
        attrs.knowledges = this.sanitizeText(attrs.knowledges);
        attrs.benefits = this.sanitizeText(attrs.benefits);
        attrs.recruiter = this.sanitizeText(attrs.recruiter);
        
        // Sanitizar campos numéricos
        attrs.territory_commune_id = this.sanitizeId(attrs.territory_commune_id);
        attrs.position_id = this.sanitizeId(attrs.position_id);
        attrs.head_id = this.sanitizeId(attrs.head_id);
        attrs.employee_replacement_id = this.sanitizeId(attrs.employee_replacement_id);
        attrs.management_id = this.sanitizeId(attrs.management_id);
        attrs.education_level = this.sanitizeEducationLevel(attrs.education_level);
        attrs.experience_years = this.sanitizeNumber(attrs.experience_years);
        
        // Sanitizar fecha
        attrs.desactivate_at = this.sanitizeDate(attrs.desactivate_at);
        
        // Sanitizar buk_id (puede contener caracteres especiales pero limitados)
        if (attrs.buk_id) {
          attrs.buk_id = String(attrs.buk_id).replace(/[^0-9a-zA-Z_-]/g, '');
        }
        
        // Sanitizar publication_questions_attributes
        if (attrs.publication_questions_attributes && Array.isArray(attrs.publication_questions_attributes)) {
          attrs.publication_questions_attributes = attrs.publication_questions_attributes.map(q => ({
            ...q,
            name: this.sanitizeText(q.name),
            answer_type: this.sanitizeText(q.answer_type),
            options: typeof q.options === 'string' ? 
              JSON.stringify(this.sanitizeOptions(JSON.parse(q.options || '[]'))) : 
              JSON.stringify(this.sanitizeOptions(q.options || []))
          }));
        }
      }
    }

    return sanitized;
  }

  // Valida que los datos cumplan con los requisitos de seguridad
  static validateData(data) {
    const errors = [];
    
    if (!data || typeof data !== 'object') {
      errors.push('Datos inválidos');
      return { isValid: false, errors };
    }

    // Validar que no contenga patrones realmente peligrosos
    const dataString = JSON.stringify(data).toLowerCase();
    
    // Solo detectar patrones realmente peligrosos, no palabras comunes
    const dangerousPatterns = [
      { pattern: /<script[^>]*>/gi, message: 'Etiquetas script detectadas' },
      { pattern: /javascript:/gi, message: 'Enlaces javascript detectados' },
      { pattern: /on\w+\s*=/gi, message: 'Event handlers detectados' },
      { pattern: /<iframe[^>]*>/gi, message: 'Iframes detectados' },
      { pattern: /<object[^>]*>/gi, message: 'Objetos embebidos detectados' },
      { pattern: /<embed[^>]*>/gi, message: 'Embebidos detectados' },
      { pattern: /expression\s*\(/gi, message: 'Expresiones CSS detectadas' },
      { pattern: /eval\s*\(/gi, message: 'Código eval detectado' },
      { pattern: /import\s*\(/gi, message: 'Imports dinámicos detectados' }
    ];

    for (const { pattern, message } of dangerousPatterns) {
      if (pattern.test(dataString)) {
        errors.push(message);
        break; // Solo reportar el primer patrón encontrado
      }
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }
}

export default SecuritySanitizer;
